<template>
  <div class="min-h-screen bg-gray-50">
    <Header />
    <main class="p-6">
      <div class="max-w-4xl mx-auto">
        <div class="flex items-center justify-between mb-4">
          <h1 class="text-2xl font-bold">Link: {{ shortCode }}</h1>
          <router-link to="/links" class="btn btn-secondary">Back to Links</router-link>
        </div>

        <ErrorMessage v-if="error" :message="error" title="Error Loading Link" class="mb-8" />

        <div v-if="isLoading" class="text-center py-12 mb-8">
          <div class="spinner mx-auto"></div>
        </div>

        <div v-else-if="link" class="space-y-8">
          <div class="card mb-8">
            <div class="card-body">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="text-sm text-gray-600">Short Code</div>
                  <div class="font-mono">{{ link.shortCode }}</div>
                </div>
                <div>
                  <div class="text-sm text-gray-600">Tenant</div>
                  <div class="font-mono">{{ link.tenantId }}</div>
                </div>
                <div class="md:col-span-2">
                  <div class="text-sm text-gray-600">Redirect URL</div>
                  <div class="truncate" :title="link.redirectURL">{{ link.redirectURL }}</div>
                </div>
                <div>
                  <div class="text-sm text-gray-600">Clicks</div>
                  <div class="font-semibold">{{ link.clickCount }}</div>
                </div>
                <div>
                  <div class="text-sm text-gray-600">Status</div>
                  <div>
                    <span :class="link.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" class="px-2 py-1 text-xs rounded-full">
                      {{ link.isActive ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                </div>
                <div>
                  <div class="text-sm text-gray-600">Created</div>
                  <div>{{ formatDate(link.createdAt) }}</div>
                </div>
                <div>
                  <div class="text-sm text-gray-600">Updated</div>
                  <div>{{ formatDate(link.updatedAt) }}</div>
                </div>
                <div v-if="link.expiresAt">
                  <div class="text-sm text-gray-600">Expires</div>
                  <div>{{ formatDate(link.expiresAt) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Header from '@/components/common/Header.vue'
import ErrorMessage from '@/components/common/ErrorMessage.vue'
import { linksService } from '@/services/links'
import type { Link } from '@/types'

const route = useRoute()
const shortCode = String(route.params.shortCode || '')
const link = ref<Link | null>(null)
const isLoading = ref(false)
const error = ref('')

const formatDate = (ts: number) => new Date(ts).toLocaleString()

onMounted(async () => {
  try {
    isLoading.value = true
    const res = await linksService.getLink(shortCode)
    link.value = res
  } catch (e: any) {
    error.value = e?.message || 'Failed to load link'
  } finally {
    isLoading.value = false
  }
})
</script>
